import { CheckCircle2, CircleAlert } from 'lucide-react'
import { useState, type CSSProperties } from 'react'

interface ProfileAvatarProps {
  name: string
  initials: string
  /** null when verification status is unknown (no session data); no badge is drawn then. */
  emailVerified: boolean | null
  /** Any CSS length (e.g. "3rem"); overrides the default `--avatar-size` from index.css. */
  size?: string
}

/**
 * Hexagonal avatar. Hovering starts one spin that always plays to the end,
 * even if the pointer leaves right away; hovering again mid-spin is ignored.
 * Timing and size live in the `.profile-avatar` block of index.css.
 */
export function ProfileAvatar({
  name,
  initials,
  emailVerified,
  size,
}: ProfileAvatarProps) {
  const [spinning, setSpinning] = useState(false)

  return (
    <div
      className="profile-avatar shrink-0 self-start sm:self-center"
      role="img"
      aria-label={`${name} avatar`}
      data-spinning={spinning}
      style={size ? ({ '--avatar-size': size } as CSSProperties) : undefined}
      onMouseEnter={() => setSpinning(true)}
      onAnimationEnd={(event) => {
        if (event.animationName === 'avatar-spin') setSpinning(false)
      }}
    >
      <span aria-hidden="true" className="profile-avatar-ring">
        <span className="profile-avatar-sweep">
          <span className="profile-avatar-spin" />
        </span>
      </span>
      <span aria-hidden="true" className="profile-avatar-face">
        {initials}
      </span>
      {emailVerified !== null && (
        <span className="absolute right-0 bottom-1 grid size-6 place-items-center rounded-full bg-raised ring-2 ring-raised">
          {emailVerified ? (
            <CheckCircle2
              className="size-5 text-status-success"
              aria-label="Email verified"
            />
          ) : (
            <CircleAlert
              className="size-5 text-status-assessed"
              aria-label="Email not verified"
            />
          )}
        </span>
      )}
    </div>
  )
}
